import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { attributeRegistry } from '@shared/assessment-definition/dist/src/attributes'
import type { AttributeKey } from '@shared/assessment-definition/dist/src/attributes'
import type {
  GuidedAnswer,
  GuidedAnswerAttributes,
  GuidedModeDefinition,
  GuidedModeQuestionDefinition,
  GuidedModeStep,
  GuidedModeView,
} from '../types/guided-mode.types'
import { attributeRows, stepKey } from '../utils/step-engine'
import type { GuidedModeAnswerAttributes } from '../utils/step-engine'

interface UseGuidedAttributeStepOptions {
  answers: Ref<Record<string, GuidedAnswer>>
  answerAttributes: ComputedRef<GuidedModeAnswerAttributes>
  currentStep: ComputedRef<GuidedModeStep | null>
  definition: ComputedRef<GuidedModeDefinition | null>
  view: Ref<GuidedModeView>
  setAnswerAttributes: (conceptId: string, attributes: GuidedAnswerAttributes) => void
  questionTitle: (question?: GuidedModeQuestionDefinition) => string
  translate: (token: string, fallback: string) => string
  onAttributeChanged: () => void
}

export interface GuidedAttributeRow {
  conceptId: string
  prompt: string
  options: { value: string; label: string }[]
  selectedValues: string[]
}

export function useGuidedAttributeStep(options: UseGuidedAttributeStepOptions) {
  const {
    answers, answerAttributes, currentStep, definition, view, setAnswerAttributes, questionTitle,
    translate, onAttributeChanged,
  } = options
  // Rows not yet changed by hand on the step on screen; they follow the first row.
  const followers = ref<string[]>([])

  const currentAttributeStep = computed(() => {
    const step = currentStep.value
    return view.value === 'question' && step?.kind === 'attribute' && step.attribute ? step : null
  })
  const attributeSteps = computed(() => (definition.value?.assessment?.steps || [])
    .filter(step => step.kind === 'attribute'))
  const attributePrompt = computed(() => {
    const attribute = currentAttributeStep.value?.attribute
    return attribute
      ? translate(attributeRegistry[attribute].promptToken, 'Which eye is affected?')
      : ''
  })
  const attributeStepRows = computed<GuidedAttributeRow[]>(() => {
    const step = currentAttributeStep.value
    if (!step?.attribute) return []
    const attribute = step.attribute
    const { values, responseTokens } = attributeRegistry[attribute]
    const rowOptions = values.map(value => ({
      value,
      label: translate(responseTokens[value], value),
    }))
    return attributeRows(step, answers.value).map(row => {
      const value = valueOf(row.conceptId, attribute)
      return {
        conceptId: row.conceptId,
        prompt: questionTitle(definition.value?.questions[row.questionId]),
        options: rowOptions,
        selectedValues: value ? [value] : [],
      }
    })
  })

  function valueOf(conceptId: string, attribute: AttributeKey): string | undefined {
    return answerAttributes.value[conceptId]?.[attribute]
  }

  function writeAttribute(conceptId: string, attribute: AttributeKey, value: string | undefined) {
    const previous = valueOf(conceptId, attribute)
    if (previous === value) return
    setAnswerAttributes(conceptId, {
      ...answerAttributes.value[conceptId],
      [attribute]: value,
    } as GuidedAnswerAttributes)
    onAttributeChanged()
    if (!previous) return
    for (const step of attributeSteps.value) {
      if (step.initialFrom !== conceptId || step.attribute !== attribute) continue
      for (const item of step.items || []) {
        if (valueOf(item.conceptId, attribute) === previous) {
          writeAttribute(item.conceptId, attribute, undefined)
        }
      }
    }
  }

  function selectAttribute(conceptId: string, value: string) {
    const step = currentAttributeStep.value
    if (!step?.attribute) return
    const attribute = step.attribute
    writeAttribute(conceptId, attribute, value)
    if (conceptId === attributeRows(step, answers.value)[0]?.conceptId) {
      followers.value.forEach(follower => writeAttribute(follower, attribute, value))
    } else {
      followers.value = followers.value.filter(follower => follower !== conceptId)
    }
  }

  watch(() => (currentAttributeStep.value ? stepKey(currentAttributeStep.value) : ''), key => {
    followers.value = []
    const step = currentAttributeStep.value
    if (!key || !step?.attribute) return
    const attribute = step.attribute
    const rows = attributeRows(step, answers.value)
    const emptyRows = rows.filter(row => !valueOf(row.conceptId, attribute))
    followers.value = emptyRows.filter(row => row !== rows[0]).map(row => row.conceptId)
    const initial = step.initialFrom ? valueOf(step.initialFrom, attribute) : undefined
    const first = initial || valueOf(rows[0]?.conceptId || '', attribute)
    if (!first) return
    const filled = initial ? emptyRows.map(row => row.conceptId) : followers.value
    filled.forEach(conceptId => writeAttribute(conceptId, attribute, first))
  }, { immediate: true })

  return { currentAttributeStep, attributePrompt, attributeStepRows, selectAttribute }
}
